import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../../../../i18n';
import { request } from '../../../shared/services/api';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { DashboardCharts } from '../components/DashboardCharts';
import { DashboardMetrics } from '../components/DashboardMetrics';
import { DashboardTables } from '../components/DashboardTables';
import {
  C,
  TIP_COLORS,
  pct,
  semaforo,
  abreviar,
  abreviarPersona,
  calcularMadurez,
  emptyFilters,
  FICHA_SECTIONS,
  type ProcessDashboardData,
  type Filters,
  type FilterKey,
} from '../components/dashboard-utils';

const STYLE_ID = 'process-dashboard-style';
function useInjectStyles() {
  useEffect(() => {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      .pd-root *:focus, .pd-root *:focus-visible, .pd-root *:active {
        outline: none !important; box-shadow: none !important;
        -webkit-tap-highlight-color: transparent !important;
      }
      .pd-root .recharts-wrapper, .pd-root .recharts-surface { outline: none !important; }
      .pd-root .pd-table td { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 0; }
    `;
    document.head.appendChild(style);
    return () => {
      document.getElementById(STYLE_ID)?.remove();
    };
  }, []);
}

export function ProcessDashboard() {
  const navigate = useNavigate();
  const { lang } = useI18n();

  const [data, setData] = useState<ProcessDashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [flt, setFlt] = useState<Filters>(emptyFilters);

  const ctrlRef = useRef(false);
  useInjectStyles();

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey) ctrlRef.current = true;
    };
    const up = () => {
      ctrlRef.current = false;
    };
    window.addEventListener('keydown', down);
    window.addEventListener('keyup', up);
    return () => {
      window.removeEventListener('keydown', down);
      window.removeEventListener('keyup', up);
    };
  }, []);

  useEffect(() => {
    request<{ data: ProcessDashboardData }>('/process-map/dashboard')
      .then((json) => {
        setData(json.data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Error');
        setLoading(false);
      });
  }, [lang]);

  const has =
    flt.tipologia.size > 0 ||
    flt.grupoNombre.size > 0 ||
    flt.direccion.size > 0 ||
    flt.representante.size > 0 ||
    flt.rol.size > 0 ||
    flt.proceso.size > 0;

  const tog = useCallback((k: FilterKey, v: string) => {
    setFlt((prev) => {
      const ns = new Set(prev[k]);
      if (ctrlRef.current) {
        if (ns.has(v)) ns.delete(v);
        else ns.add(v);
      } else {
        if (ns.size === 1 && ns.has(v)) ns.clear();
        else {
          ns.clear();
          ns.add(v);
        }
      }
      return { ...prev, [k]: ns };
    });
  }, []);

  const clr = useCallback(() => setFlt(emptyFilters()), []);

  const dim = (k: FilterKey, v: string): React.CSSProperties =>
    has && flt[k].size > 0 && !flt[k].has(v)
      ? { opacity: 0.18, transition: 'opacity .2s' }
      : { opacity: 1, transition: 'opacity .2s' };

  const allProcs = data?.procesos ?? [];
  const allElements = data?.elements ?? [];

  const matched = useMemo(() => {
    if (!has) return allProcs;
    return allProcs.filter((p) => {
      if (flt.tipologia.size > 0 && !flt.tipologia.has(p.tipologia)) return false;
      if (flt.grupoNombre.size > 0 && !flt.grupoNombre.has(p.grupoNombre)) return false;
      if (
        flt.direccion.size > 0 &&
        !flt.direccion.has((p.direccionResponsable || 'Sin asignar').trim())
      )
        return false;
      if (
        flt.representante.size > 0 &&
        !flt.representante.has((p.representante || 'Sin asignar').trim())
      )
        return false;
      if (flt.rol.size > 0) {
        if (!p.rolesInvolucrados?.some((r) => flt.rol.has(r.rol))) return false;
      }
      if (flt.proceso.size > 0 && !flt.proceso.has(p.codigo)) return false;
      return true;
    });
  }, [allProcs, flt, has]);

  const matchedIds = useMemo(() => new Set(matched.map((p) => p.id)), [matched]);

  const tableProcs = useMemo(() => {
    const hasOther =
      flt.tipologia.size > 0 ||
      flt.grupoNombre.size > 0 ||
      flt.direccion.size > 0 ||
      flt.representante.size > 0 ||
      flt.rol.size > 0;
    if (!hasOther) return allProcs;
    return allProcs.filter((p) => {
      if (flt.tipologia.size > 0 && !flt.tipologia.has(p.tipologia)) return false;
      if (flt.grupoNombre.size > 0 && !flt.grupoNombre.has(p.grupoNombre)) return false;
      if (
        flt.direccion.size > 0 &&
        !flt.direccion.has((p.direccionResponsable || 'Sin asignar').trim())
      )
        return false;
      if (
        flt.representante.size > 0 &&
        !flt.representante.has((p.representante || 'Sin asignar').trim())
      )
        return false;
      if (flt.rol.size > 0) {
        if (!p.rolesInvolucrados?.some((r) => flt.rol.has(r.rol))) return false;
      }
      return true;
    });
  }, [allProcs, flt]);

  const filteredElements = useMemo(
    () => (has ? allElements.filter((d) => matchedIds.has(d.procesoId)) : allElements),
    [allElements, matchedIds, has],
  );

  const tot = allProcs.length;
  const mLen = matched.length;

  const peF = matched.filter((p) => p.tipologia === 'PE').length;
  const poF = matched.filter((p) => p.tipologia === 'PO').length;
  const psF = matched.filter((p) => p.tipologia === 'PS').length;

  const conResp = matched.filter((p) => p.responsableId).length;
  const conRepr = matched.filter((p) => p.representanteId).length;
  const conDir = matched.filter((p) => p.direccionId).length;
  const totalDocs = matched.reduce((s, p) => s + p.totalDocumentos, 0);
  const docsPubl = matched.reduce((s, p) => s + (p.docsPublicados ?? 0), 0);
  const docsPend = totalDocs - docsPubl;
  const conDocs = matched.filter((p) => p.totalDocumentos > 0).length;
  const pctDocumentado = pct(conDocs, mLen);

  const grupos = useMemo(() => [...new Set(allProcs.map((p) => p.grupoNombre))].length, [allProcs]);

  const aggDir = useMemo(() => {
    const x: Record<string, number> = {};
    allProcs.forEach((p) => {
      const n = (p.direccionResponsable || 'Sin asignar').trim();
      x[n] = (x[n] || 0) + 1;
    });
    return Object.entries(x)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [allProcs]);

  const aggGrp = useMemo(() => {
    const x: Record<string, { name: string; value: number; tip: string }> = {};
    allProcs.forEach((p) => {
      if (!x[p.grupoNombre]) x[p.grupoNombre] = { name: p.grupoNombre, value: 0, tip: p.tipologia };
      x[p.grupoNombre].value++;
    });
    return Object.values(x).sort((a, b) => b.value - a.value);
  }, [allProcs]);

  const tableReprData = useMemo(() => {
    const hasOther =
      flt.tipologia.size > 0 ||
      flt.grupoNombre.size > 0 ||
      flt.direccion.size > 0 ||
      flt.rol.size > 0 ||
      flt.proceso.size > 0;
    const base = hasOther
      ? allProcs.filter((p) => {
          if (flt.tipologia.size > 0 && !flt.tipologia.has(p.tipologia)) return false;
          if (flt.grupoNombre.size > 0 && !flt.grupoNombre.has(p.grupoNombre)) return false;
          if (
            flt.direccion.size > 0 &&
            !flt.direccion.has((p.direccionResponsable || 'Sin asignar').trim())
          )
            return false;
          if (flt.rol.size > 0 && !p.rolesInvolucrados?.some((r) => flt.rol.has(r.rol)))
            return false;
          if (flt.proceso.size > 0 && !flt.proceso.has(p.codigo)) return false;
          return true;
        })
      : allProcs;
    const x: Record<string, number> = {};
    base.forEach((p) => {
      const n = (p.representante || 'Sin asignar').trim();
      x[n] = (x[n] || 0) + 1;
    });
    return Object.entries(x)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value);
  }, [allProcs, flt]);

  const tipData = useMemo(
    () => [
      { name: 'PE', value: peF, fill: TIP_COLORS.PE },
      { name: 'PO', value: poF, fill: TIP_COLORS.PO },
      { name: 'PS', value: psF, fill: TIP_COLORS.PS },
    ],
    [peF, poF, psF],
  );

  const fichaStats = useMemo(
    () =>
      FICHA_SECTIONS.map((s) => ({
        name: s.charAt(0).toUpperCase() + s.slice(1),
        count: matched.filter((p) => (p[s] as number) > 0).length,
        total: mLen,
      })),
    [matched, mLen],
  );

  const totalIndicadores = matched.reduce((s, p) => s + p.indicadores, 0);
  const conIndicador = matched.filter((p) => p.indicadores > 0).length;
  const indPorTipo = useMemo(() => {
    const x: Record<string, number> = {};
    matched.forEach((p) => {
      if (p.indicadoresPorTipo) {
        for (const [tipo, cnt] of Object.entries(p.indicadoresPorTipo)) {
          x[tipo] = (x[tipo] || 0) + cnt;
        }
      }
    });
    return Object.entries(x)
      .map(([tipo, cnt]) => ({ tipo, cnt }))
      .sort((a, b) => b.cnt - a.cnt);
  }, [matched]);

  const aggRoles = useMemo(() => {
    const x: Record<string, number> = {};
    matched.forEach((p) => {
      if (p.rolesInvolucrados) {
        p.rolesInvolucrados.forEach((r) => {
          const n = r.rol.trim();
          x[n] = (x[n] || 0) + 1;
        });
      }
    });
    return Object.entries(x)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  }, [matched]);

  const madurez = useMemo(() => calcularMadurez(matched), [matched]);

  const gDocumentado = pct(conDocs, mLen);
  const gPublicados = totalDocs > 0 ? pct(docsPubl, totalDocs) : 0;

  const activePills = useMemo(() => {
    const pills: Array<{ key: FilterKey; value: string; label: string }> = [];
    for (const v of flt.tipologia) pills.push({ key: 'tipologia', value: v, label: `Tipo: ${v}` });
    for (const v of flt.grupoNombre)
      pills.push({ key: 'grupoNombre', value: v, label: `Grupo: ${abreviar(v, 20)}` });
    for (const v of flt.direccion)
      pills.push({ key: 'direccion', value: v, label: `Dir: ${abreviar(v, 18)}` });
    for (const v of flt.representante)
      pills.push({ key: 'representante', value: v, label: `Repr: ${abreviarPersona(v, 16)}` });
    for (const v of flt.rol) pills.push({ key: 'rol', value: v, label: `Rol: ${abreviar(v, 16)}` });
    for (const v of flt.proceso) pills.push({ key: 'proceso', value: v, label: `Proceso: ${v}` });
    return pills;
  }, [flt]);

  const removePill = useCallback((k: FilterKey, v: string) => {
    setFlt((prev) => {
      const ns = new Set(prev[k]);
      ns.delete(v);
      return { ...prev, [k]: ns };
    });
  }, []);

  const madurezData = [
    {
      name: 'Indicadores',
      value: madurez.indicadores,
      fill:
        madurez.indicadores >= 80
          ? C.statusOk
          : madurez.indicadores >= 50
            ? C.statusWarn
            : C.statusError,
    },
    {
      name: 'Caracteriz.',
      value: madurez.caracterizacion,
      fill:
        madurez.caracterizacion >= 80
          ? C.statusOk
          : madurez.caracterizacion >= 50
            ? C.statusWarn
            : C.statusError,
    },
    {
      name: 'Document.',
      value: madurez.documentacion,
      fill:
        madurez.documentacion >= 80
          ? C.statusOk
          : madurez.documentacion >= 50
            ? C.statusWarn
            : C.statusError,
    },
    {
      name: 'Gobernanza',
      value: madurez.gobernanza,
      fill:
        madurez.gobernanza >= 80
          ? C.statusOk
          : madurez.gobernanza >= 50
            ? C.statusWarn
            : C.statusError,
    },
  ];

  const navToLevel3 = (processId: string) => navigate(`/process-map/process/${processId}`);
  const navToDocInLevel3 = (processId: string, docCodigo: string) =>
    navigate(`/process-map/process/${processId}?doc=${encodeURIComponent(docCodigo)}`);

  const containerH = 'calc(100vh - 88px)';
  const baseBg: React.CSSProperties = {
    background: 'var(--wl-background, #ECEEF2)',
    fontFamily: "'DM Sans',sans-serif",
  };

  if (loading) {
    return (
      <div
        style={{
          height: containerH,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          ...baseBg,
        }}
      >
        <LoadingSpinner text="Cargando dashboard de procesos..." size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: containerH,
          ...baseBg,
        }}
      >
        <div style={{ textAlign: 'center', color: C.rojo }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
            Error al cargar datos
          </div>
          <div style={{ fontSize: 13 }}>{error}</div>
        </div>
      </div>
    );
  }

  if (!data || tot === 0) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: containerH,
          ...baseBg,
          color: C.go,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Sin datos</div>
          <div style={{ fontSize: 13 }}>No hay procesos registrados en el sistema.</div>
        </div>
      </div>
    );
  }

  const kpiItems = [
    { key: 'tot', value: has ? mLen : tot, label: 'Procesos', color: C.statusInfo },
    { key: 'pe', value: peF, label: 'Estrategicos', color: TIP_COLORS.PE },
    { key: 'po', value: poF, label: 'Operativos', color: TIP_COLORS.PO },
    { key: 'ps', value: psF, label: 'Soporte', color: TIP_COLORS.PS },
    { key: 'grp', value: grupos, label: 'Grupos', color: C.statusPurple },
    {
      key: 'resp',
      value: `${conResp}/${mLen}`,
      label: 'Con responsable',
      color: semaforo(pct(conResp, mLen)),
    },
    {
      key: 'repr',
      value: `${conRepr}/${mLen}`,
      label: 'Con representante',
      color: semaforo(pct(conRepr, mLen)),
    },
    {
      key: 'dir',
      value: `${conDir}/${mLen}`,
      label: 'Con direccion',
      color: semaforo(pct(conDir, mLen)),
    },
    { key: 'docs', value: totalDocs, label: 'Documentos', color: C.statusNeutral },
    {
      key: 'pdoc',
      value: `${pctDocumentado}%`,
      label: '% Documentado',
      color: semaforo(pctDocumentado),
    },
    { key: 'publ', value: `${docsPubl}/${totalDocs}`, label: 'Publicados', color: C.statusOk },
    {
      key: 'pend',
      value: docsPend,
      label: 'Pendientes',
      color: docsPend > 0 ? C.statusError : C.statusOk,
    },
  ];

  return (
    <div
      className="pd-root"
      style={{
        display: 'grid',
        gridTemplateRows: '66px 215px 175px 22px minmax(0, 1fr)',
        gridTemplateColumns: '1fr 1fr 1fr',
        gap: 5,
        height: containerH,
        padding: '6px 20px 12px 20px',
        boxSizing: 'border-box',
        overflow: 'hidden',
        ...baseBg,
      }}
    >
      {/* ROW 1: 12 KPI Cards */}
      <div
        style={{
          gridColumn: '1 / -1',
          display: 'grid',
          gridTemplateColumns: 'repeat(12, 1fr)',
          gap: 4,
        }}
      >
        {kpiItems.map((item) => (
          <div
            key={item.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              height: 58,
              background: '#fff',
              borderRadius: 10,
              border: `0.5px solid ${C.gm}`,
              borderLeftWidth: 5,
              borderLeftColor: item.color,
            }}
          >
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 22,
                  fontWeight: 700,
                  fontFamily: "'JetBrains Mono',monospace",
                  color: item.color,
                  lineHeight: 1,
                }}
              >
                {item.value}
              </div>
              <div
                style={{
                  fontSize: 9,
                  fontWeight: 600,
                  color: '#666',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                }}
              >
                {item.label}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* ROW 2: 3 overview charts */}
      <DashboardCharts
        aggDir={aggDir}
        aggGrp={aggGrp}
        tipData={tipData}
        mLen={mLen}
        tog={tog}
        dim={dim}
      />

      {/* ROW 3: 5 metric cards */}
      <DashboardMetrics
        fichaStats={fichaStats}
        totalIndicadores={totalIndicadores}
        conIndicador={conIndicador}
        mLen={mLen}
        indPorTipo={indPorTipo}
        aggRoles={aggRoles}
        gDocumentado={gDocumentado}
        gPublicados={gPublicados}
        madurez={madurez}
        madurezData={madurezData}
        tog={tog}
        dim={dim}
      />

      {/* ROW 4 + ROW 5: pills + tables */}
      <DashboardTables
        tableReprData={tableReprData}
        tableProcs={tableProcs}
        filteredElements={filteredElements}
        allProcs={allProcs}
        flt={flt}
        has={has}
        activePills={activePills}
        tog={tog}
        dim={dim}
        removePill={removePill}
        clr={clr}
        navToLevel3={navToLevel3}
        navToDocInLevel3={navToDocInLevel3}
      />
    </div>
  );
}
